import { useState } from 'react';
import { MessageSquareIcon, BarChart3Icon } from 'lucide-react';
import type { Comment, CommentCategory } from '@/types/database.types';
import CommentComponent from '@/components/Comment';
import Input from '@/components/form/Input';
import Button from '@/components/form/Button';
import FormField from '@/components/form/FormField';

interface CommentSectionProps {
  comments: Comment[];
  onAddComment: (content: string, category: CommentCategory) => Promise<void>;
  onDeleteComment: (id: string) => void;
  onReaction: (id: string, type: 'like' | 'dislike') => void;
  userId: string | undefined;
}

export default function CommentSection({
  comments,
  onAddComment,
  onDeleteComment,
  onReaction,
  userId
}: CommentSectionProps) {
  const [comment, setComment] = useState('');
  const [commentCategory, setCommentCategory] = useState<CommentCategory>('rational');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!comment.trim()) return;

    try {
      await onAddComment(comment, commentCategory);
      setComment('');
    } catch (error) {
      console.error('Failed to add comment:', error);
    }
  };

  return (
    <div className="border-t pt-8">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-gray-900">
          Community Feedback
        </h2>
        <div className="flex items-center space-x-2 text-sm text-gray-600">
          <BarChart3Icon className="h-4 w-4" />
          <span>{comments.length} comments</span>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="mb-8">
        <FormField 
          label="Your Comment" 
          required
        >
          <div className="space-y-4">
            <Input
              as="textarea"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              className="w-full rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              rows={3}
              placeholder="Share your thoughts..."
              required
            />

            <div className="flex items-center space-x-4">
              <select
                value={commentCategory}
                onChange={(e) => setCommentCategory(e.target.value as CommentCategory)}
                className="rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              >
                <option value="emotional">Emotional</option>
                <option value="rational">Rational</option>
                <option value="economic">Economic</option>
                <option value="ethical">Ethical</option>
              </select>

              <Button
                type="submit"
                icon={<MessageSquareIcon className="h-4 w-4" />}
              >
                Add Comment
              </Button>
            </div>
          </div>
        </FormField>
      </form>

      <div className="space-y-6">
        {comments.map((comment) => (
          <CommentComponent
            key={comment.id}
            comment={comment}
            onDelete={onDeleteComment}
            onReaction={onReaction}
            isOwner={userId === comment.user_id}
          />
        ))}
      </div>
    </div>
  );
}